import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { Howl, Howler } from 'howler';
import { Sounds, SoundState, SoundActions } from "types/sound";

const soundFiles: Record<Sounds, string> = {
  [Sounds.Scratch]: 'sounds/scratch-card.wav',
  [Sounds.MouseClick]: 'sounds/mouse-click.mp3',
  [Sounds.PopupDrop]: 'sounds/drop.wav',
  [Sounds.CardDrop]: 'sounds/card-drop.mp3',
  [Sounds.Countdown]: 'sounds/countdown.wav',
  [Sounds.MoveUp]: 'sounds/move-up.mp3',
  [Sounds.MoveDown]: 'sounds/move-down.wav',
  [Sounds.DigitalTyping]: 'sounds/digital-typing.mp3',
  [Sounds.Spin]: 'sounds/number_spinning.wav',
  [Sounds.CardReveal]: 'sounds/card-reveal.wav',
  [Sounds.SpinStop]: 'sounds/stop.mp3',
  [Sounds.Result]: 'sounds/result.mp3',
  [Sounds.Lose]: 'sounds/lose.wav',
  [Sounds.Win]: 'sounds/win.wav',
  [Sounds.Explode]: 'sounds/explode.mp3',
  [Sounds.Flush]: 'sounds/flush.mp3',
  [Sounds.BigWin]: 'sounds/big-win.mp3',
  [Sounds.Dispense]: 'sounds/dispense.wav',
  [Sounds.Fireworks]: 'sounds/fireworks.wav',
  [Sounds.BountyWin]: 'sounds/bounty-win.wav',
  [Sounds.CoinDrop]: 'sounds/coin-drop.wav',
  [Sounds.BigLoss]: 'sounds/big-loss.wav',
  [Sounds.JackpotWin]: 'sounds/jackpot-win.wav',
  [Sounds.Woosh]: 'sounds/woosh.mp3',
  [Sounds.Fold]: 'sounds/fold.mp3',
  [Sounds.Eliminated]: 'sounds/eliminated.wav',
  [Sounds.TicketClick]: 'sounds/arcade-click.mp3',
  [Sounds.ChipUp]: 'sounds/chip-up.wav',
  [Sounds.ChipDown]: 'sounds/chip-down.wav',
};

const DEFAULT_VOLUME = 0.25;

const initialState: SoundState = {
  volume: DEFAULT_VOLUME,
  sounds: {} as Record<Sounds, Howl>,
}

export const useSoundStore = create<SoundState & SoundActions>()(
  persist((set, get) => ({
      ...initialState,
      initialize: () => {
        Howler.usingWebAudio = false;
        Howler.volume(get().volume);

        const sounds: Record<Sounds, Howl> = {} as Record<Sounds, Howl>;

        for (let sound in soundFiles) {
          const howl = new Howl({
            src: [ soundFiles[sound] ],
            preload: true,
            volume: DEFAULT_VOLUME,
          });

          sounds[sound] = howl;
        }

        set({ sounds });
      },
      toggleVolume: () => {
        get().setVolume(!get().volume ? DEFAULT_VOLUME : 0);
      },
      setVolume: (volume: number) => {
        set({ volume });
        Howler.volume(volume);
      },
      play: (sound: Sounds, rate = 1, loop = false) => {
        const s = get().sounds[sound];
        s.play();
        s.rate(rate);
        s.loop(loop);
      },
      stop: (sound: Sounds) => {
        const s = get().sounds[sound];
        s?.loop(false);
        s?.stop();
      },
      stopAll: () => {
        Howler.stop();
      },
      fadeOut: (sound: Sounds, duration = 500) => {
        const s = get().sounds[sound];
        s.fade(get().volume, 0, duration);
        s.once('fade', () => {
          // reset the volume
          setTimeout(() => {
            s.fade(get().volume, get().volume, 0);
            s.stop();
          }, 10);
        });
      },
      getHowl: (sound: Sounds) => {
        return get().sounds[sound];
      }
    }),
    {
      name: 'bh-sound',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (store) => ({
        //volume: store.volume,
      }),
    },
  ),
);

